import { useState } from 'react';
import { css } from '@emotion/react';
import { darkenColor } from './utils/darkenColor';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import { Heading } from '../../display/Heading';
import { Avatar } from '../../display/Avatar';
import { Icon } from '../../display/Icon';
import { Link } from '../../navigation/Link';
import { Menu, MenuItem } from '../../forms/Menu';
import { TagLabel } from '../../display/TagLabel';
import { useClickOutside, useZdsColor } from '../../../hooks';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import { ServiceIcons } from '../ServiceIcons';
import { useResizeObserver } from './useResizeObserver';
import { ZapCardCTA } from './ZapCardCTA';
const DEFAULT_COLOR = '#fffdf9';
const isDefaultColor = (color) => {
    return color === DEFAULT_COLOR;
};
const getDefaultComputedColors = () => ({
    backgroundColor: DEFAULT_COLOR,
    backgroundColorDark: 'rgb(0, 0, 0, 0.25)',
});
const getComputedColors = (color) => {
    if (isDefaultColor(color) || !color) {
        return getDefaultComputedColors();
    }
    try {
        return {
            backgroundColor: darkenColor(color, { contrast: 3 }),
            backgroundColorDark: darkenColor(color, {
                contrast: 4.5,
                minimumLightnessDistance: 10,
            }),
        };
    }
    catch (err) {
        return getDefaultComputedColors();
    }
};
const hasMenu = (menuItems) => {
    return (menuItems || []).length > 0;
};
const CARD_MAX_WIDTH = 375;
const SMALL_LINE_MAX_WIDTH = 770;
const MEDIUM_LINE_MAX_WIDTH = 615;
const ANIMATION_TRANSITION_VALUE = 'all 150ms ease-in-out';
const isCardLayout = (computedState) => {
    return computedState.layout === 'card';
};
const isLineLayout = (computedState) => {
    return computedState.layout === 'line';
};
const isMegaLayout = (computedState) => {
    return computedState.layout === 'mega';
};
const isCardLayoutWidth = (computedState) => {
    return computedState.width <= CARD_MAX_WIDTH;
};
const isWideLayoutWidth = (computedState) => computedState.width > SMALL_LINE_MAX_WIDTH;
const isMediumLineLayoutWidth = (computedState) => {
    return (computedState.width > CARD_MAX_WIDTH &&
        computedState.width <= MEDIUM_LINE_MAX_WIDTH);
};
const isLargeLineLayoutWidth = (computedState) => {
    return computedState.width > MEDIUM_LINE_MAX_WIDTH;
};
const computeLineLayoutGrid = (computedState, props) => {
    if (isLargeLineLayoutWidth(computedState)) {
        return css `
      grid-template-columns: max-content max-content 1fr max-content max-content max-content;
      grid-template-areas:
        'services title title title label cta menu'
        '.        ${props.authorName
            ? 'author'
            : 'meta'}  meta  meta   meta     .   .';
    `;
    }
    return css `
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas: 'services title cta';
  `;
};
const isCardOrMegaLayout = (computedState) => {
    return isCardLayout(computedState) || isMegaLayout(computedState);
};
const shouldBeColored = (computedState) => {
    return (isCardOrMegaLayout(computedState) &&
        !isDefaultColor(computedState.backgroundColor));
};
const StyleVars = {
    cardPadding: '20px',
    cardHeaderHeight: '40px',
    cardFooterHeight: '55px',
    megaPaddingLeft: '30px',
    megaPaddingTop: '38px',
    servicesHeight: '40px',
    servicesHeightMegaCard: '60px',
};
const centerLineCardStyles = css `
  display: flex;
  align-items: center;
  min-height: ${StyleVars.servicesHeight};
`;
export const Styles = labelStyles('ZapCard', {
    root: (props, computedState) => [
        css `
      min-width: 270px;
    `,
        (isCardOrMegaLayout(computedState) ||
            (isLineLayout(computedState) &&
                isMediumLineLayoutWidth(computedState))) &&
            (hasMenu(props.menuItems) || props.authorName) &&
            css `
        padding-top: ${StyleVars.cardHeaderHeight};
      `,
    ],
    inner: (computedState, props) => [
        !shouldBeColored(computedState) &&
            css `
        border: 1px solid ${Colors.neutral300};
      `,
        css `
      position: relative;
      box-shadow: none;
      transition: box-shadow 150ms ${Animation.transitionTimingFunction};
      border-radius: 5px;
      background-color: ${DEFAULT_COLOR};
      // Default to undefined.
      --ZapCard-text-color: ${Colors.neutral800};

      // This is the overlay and footer background color. A pseudo
      // element is used to facilitate using CSS Grid for the disparate
      // values for the 'layout' prop.
      &::before {
        pointer-events: none;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        transition: ${ANIMATION_TRANSITION_VALUE};
      }

      &::after {
        opacity: 0;
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 4px;
        transition: opacity 150ms ease-in-out;
      }
      @media (hover: hover) {
        &:hover,
        &:focus-within {
          box-shadow: ${Shadows.elevation5};
        }
      }
    `,
        props.shouldOverlayLink &&
            css `
        &:after {
          border: 2px solid ${Colors.blue};
        }
      `,
        isCardOrMegaLayout(computedState) &&
            css `
        --ZapCard-cta-color: ${Colors.neutral600};
        --ZapCard-cta-opacity: 0;
        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${Colors.blue};
            --ZapCard-cta-opacity: 1;
          }
        }

        &::before {
          height: ${StyleVars.cardFooterHeight};
          border-radius: 0 0 4px 4px;
          background-color: ${Colors.neutral200};
        }

        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${Colors.blue10};
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
        shouldBeColored(computedState) &&
            css `
        --ZapCard-cta-color: ${Colors.neutral100};
        --ZapCard-text-color: ${Colors.neutral100};
        background-color: ${computedState.backgroundColor};

        &::before {
          background-color: ${computedState.backgroundColorDark};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${Colors.neutral100};
          }

          &:hover,
          &:focus-within {
            box-shadow: ${Shadows.elevation10};
          }

          &:hover::before,
          &:focus-within::before {
            background-color: ${computedState.backgroundColorDark};
          }
          &:focus-within::after {
            border: 2px solid ${computedState.backgroundColorDark};
          }
        }
      `,
        isLineLayout(computedState) &&
            css `
        &::before {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
        }
        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${Colors.blue10};
          }
        }
      `,
        isLineLayout(computedState) &&
            css `
        div div div span[data-color] {
          transition: ${ANIMATION_TRANSITION_VALUE};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            div div div span[data-color] {
              background-color: ${Colors.neutral100};
            }
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
        isLineLayout(computedState) &&
            computedState.isExpanded &&
            css `
        &::before {
          content: '';
          display: none;
        }
      `,
    ],
    layout: (computedState, props) => [
        css `
      display: grid;
      position: relative;
    `,
        isCardOrMegaLayout(computedState) &&
            css `
        label: -card;
        min-height: 219px;
        grid-template-columns: 1fr max-content;
        grid-template-rows: ${StyleVars.servicesHeight} 1fr ${StyleVars.cardFooterHeight};
        gap: 10px;
        align-items: center;
        grid-template-areas:
          'services label'
          'title title'
          'meta cta';
        padding: ${StyleVars.cardPadding} ${StyleVars.cardPadding} 0;
      `,
        isMegaLayout(computedState) &&
            css `
        label: -mega;
        min-height: 345px;
        padding: ${StyleVars.megaPaddingTop} ${StyleVars.cardPadding} 0
          ${StyleVars.megaPaddingLeft};
        grid-template-rows: ${StyleVars.servicesHeightMegaCard} 1fr ${StyleVars.cardFooterHeight};
      `,
        isLineLayout(computedState) &&
            css `
        label: -line;
        ${computeLineLayoutGrid(computedState, props)}
        gap: 2px 20px;
        align-items: center;
        min-height: 80px;
        padding: ${StyleVars.cardPadding};
      `,
    ],
    titleArea: (computedState) => [
        css `
      grid-area: title;
    `,
        isLineLayout(computedState) &&
            css `
        ${centerLineCardStyles}
        align-items: inherit;
      `,
    ],
    title: (computedState) => [
        css `
      ${Typography.subHeader3Bold};
      color: var(--ZapCard-text-color, ${Colors.neutral800});
      // All of the properties below this are necessary
      // for line clamping to work. Even though the properties
      // have 'webkit' in them, they work cross-browser.
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      resize: none;
    `,
        isMegaLayout(computedState) &&
            css `
        -webkit-line-clamp: 5;
      `,
        isMegaLayout(computedState) &&
            css `
        ${isWideLayoutWidth(computedState)
                ? Typography.pageHeader7
                : Typography.pageHeader9}
      `,
        isLineLayout(computedState) &&
            css `
        ${Typography.paragraph1ShortBold}
        letter-spacing: 0 !important;
      `,
    ],
    link: css `
    ${Typography.paragraph1ShortBold};
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
    ctaArea: (computedState, props) => [
        css `
      grid-area: cta;
      color: var(--ZapCard-text-color, var(--ZapCard-cta-color));
      position: relative;
      z-index: 3;
    `,
        isCardOrMegaLayout(computedState) && [
            css `
        display: grid;
        grid-template-columns: max-content max-content;
        justify-content: end;
        align-items: center;
        transition: ${ANIMATION_TRANSITION_VALUE};
      `,
            props.shouldOverlayLink &&
                css `
          // Entire card is clickable; let these events "bleed through."
          pointer-events: none;
        `,
        ],
        isLineLayout(computedState) &&
            css `
        ${centerLineCardStyles};
      `,
    ],
    ctaLink: (computedState) => [
        css `
      display: flex;
      align-items: center;
      ${Typography.smallPrint1Bold};
      color: var(--ZapCard-cta-color);
      transition: ${ANIMATION_TRANSITION_VALUE};
      text-decoration: none;
      outline: 1px solid transparent;
      gap: 10px;
      transition: all 150ms ${Animation.transitionTimingFunction};

      // Balances out CTA right arrow icon, which
      // has 3px padding-right built in to the SVG
      padding-left: 3px;

      &:focus {
        transition: all 300ms ease-in-out;
        outline-color: ${Colors.blueJeans};
      }

      &:focus,
      &:hover {
        color: ${Colors.night};
      }
    `,
        isCardOrMegaLayout(computedState) &&
            css `
        color: var(--ZapCard-cta-color);
      `,
        isMegaLayout(computedState) &&
            css `
        ${Typography.paragraph3Bold};
      `,
    ],
    ctaIcon: (computedState) => [
        isLineLayout(computedState) &&
            css `
        display: none;
      `,
    ],
    menuArea: (computedState) => [
        css `
      position: relative;
      z-index: 4;
    `,
        computedState.isMenuOpen &&
            css `
        z-index: 5;
      `,
        isCardOrMegaLayout(computedState) &&
            css `
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
        isLineLayout(computedState) &&
            css `
        label: -line;
        grid-area: menu;
        // Tighten gutter
        margin-left: -10px;
        ${centerLineCardStyles};
      `,
        isLineLayout(computedState) &&
            isMediumLineLayoutWidth(computedState) &&
            css `
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    ],
    menuToggle: css `
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.neutral600};
    &:hover,
    &:focus,
    &[aria-expanded='true'] {
      color: ${Colors.blue};
    }
  `,
    menu: (computedState) => [
        css `
      position: absolute;
      width: 270px;
      background: ${Colors.neutral100};
      border: 1px solid ${Colors.neutral400};
      border-radius: 3px;
      box-shadow: ${Shadows.elevation20};
      // Subtract a pixel to ensure card coverage.
      top: calc(100% - 1px);
      right: -1px;
    `,
        isLineLayout(computedState) &&
            isLargeLineLayoutWidth(computedState) &&
            css `
        top: 100%;
        right: calc(-${StyleVars.cardPadding} - 1px);
      `,
    ],
    menuInner: css `
    padding: 10px;
    color: ${Colors.neutral700};
  `,
    menuItemInner: css `
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 8px;
    align-items: center;
  `,
    menuItemText: css `
    overflow: hidden;
    text-overflow: ellipsis;
  `,
    labelArea: (computedState) => [
        css `
      display: flex;
      grid-area: label;
      position: relative;
      z-index: 3;
      gap: 10px;
      align-items: center;
    `,
        isCardOrMegaLayout(computedState) &&
            css `
        text-align: right;
        justify-content: end;
      `,
        isLineLayout(computedState) &&
            css `
        ${centerLineCardStyles};
        gap: 20px;
      `,
        isLineLayout(computedState) &&
            isMediumLineLayoutWidth(computedState) &&
            css `
        display: none;
      `,
    ],
    servicesArea: [
        css `
      grid-area: services;
      position: relative;
      z-index: 3;
      align-self: baseline;
    `,
    ],
    metaInfoArea: (computedState) => [
        css `
      grid-area: meta;
      ${Typography.smallPrint3Bold};
      font-size: 14px;
      line-height: 20px;
      color: var(--ZapCard-text-color, ${Colors.neutral800});
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `,
        isMegaLayout(computedState) &&
            css `
        font-size: 16px;
        line-height: 22px;
      `,
        isCardOrMegaLayout(computedState) &&
            css `
        pointer-events: none;
      `,
        isLineLayout(computedState) &&
            css `
        line-height: 20px;
      `,
        isLineLayout(computedState) &&
            isMediumLineLayoutWidth(computedState) &&
            css `
        display: none;
      `,
    ],
    authorArea: (computedState) => [
        css `
      ${Typography.smallPrint1};
      gap: 10px;
    `,
        isCardOrMegaLayout(computedState) &&
            css `
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
        isLineLayout(computedState) &&
            css `
        label: -line;
        grid-area: author;
        ${centerLineCardStyles};
      `,
        isLineLayout(computedState) &&
            isLargeLineLayoutWidth(computedState) &&
            css `
        gap: 5px;
        font-size: 14px;
        min-height: auto;
        position: relative;

        // Simple but somewhat magical way to add middot.
        // Gets the job done, but might refactor if more
        // of these middot separators are needed later.
        &:not(:empty)::after {
          content: '•';
          position: absolute;
          font-size: 16px;
          right: -14px;
          bottom: 2px;
          opacity: 0.4;
        }
      `,
        isLineLayout(computedState) &&
            isMediumLineLayoutWidth(computedState) &&
            css `
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    ],
    authorContent: (computedState) => [
        css `
      display: grid;
      grid-template-columns: min-content 1fr;
      gap: inherit;
      align-items: center;
      color: ${Colors.neutral700};
    `,
        css `
      position: relative;
      z-index: ${isLineLayout(computedState) &&
            !isMediumLineLayoutWidth(computedState)
            ? 3
            : 4};
    `,
    ],
    authorByline: (computedState) => [
        css `
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 22ch;
    `,
        isLineLayout(computedState) &&
            css `
        max-width: 15ch;
      `,
    ],
    authorName: css `
    color: ${Colors.neutral700};
    font-weight: 700;
  `,
    summary: css `
    display: block;
    cursor: pointer;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
    detailsContent: css `
    position: relative;
    z-index: 3;
    padding: 0 20px 20px;
  `,
    detailsHref: css `
    z-index: 3;
  `,
});
const defaultRenderAuthor = (props, computedState) => {
    if (!props.authorName) {
        return null;
    }
    const [first, ...rest] = props.authorName.split(/\s+/);
    const last = rest.pop();
    const lastInitial = last ? `${last[0]}.` : '';
    const name = `${first} ${lastInitial}`.trim();
    const author = (isolateComponent("p", { "aria-hidden": true, css: Styles.authorByline(computedState) },
        "by ",
        isolateComponent("span", { css: Styles.authorName }, name)));
    return (isolateComponent("div", { "aria-label": `by ${props.authorName}`, css: Styles.authorContent(computedState) },
        isolateComponent(Avatar, { ariaHidden: true, isBlock: true, name: props.authorName, size: isLineLayout(computedState) && isLargeLineLayoutWidth(computedState)
                ? 18
                : 25, url: props.authorAvatarUrl }),
        isolateComponent(TooltipWrapper, { content: props.authorName, position: "southwest", shouldCorrectPosition: false }, () => author)));
};
const defaultRenderLabel = (props, computedState) => {
    const color = shouldBeColored(computedState) ? 'white' : 'info';
    return (props.label && (isolateComponent(TooltipWrapper, { content: props.labelTooltip, position: "southeast", shouldCorrectPosition: false }, () => isolateComponent(TagLabel, { color: color }, props.label))));
};
const defaultRenderMenu = (props, computedState, { closeMenu }) => {
    var _a, _b, _c;
    const hasDetailsMenuItem = props.detailsLink &&
        isLineLayout(computedState) &&
        isMediumLineLayoutWidth(computedState);
    if (!hasDetailsMenuItem && !hasMenu(props.menuItems)) {
        return null;
    }
    const renderMenuItem = (item) => {
        const onClick = (e) => {
            item.onClick && item.onClick(e, item);
            closeMenu();
        };
        const icon = item.icon && isolateComponent(Icon, { name: item.icon, size: 24 });
        return (isolateComponent(MenuItem, { href: item.href, key: item.text, onClick: onClick, rel: item.rel },
            isolateComponent("span", { css: Styles.menuItemInner },
                icon,
                isolateComponent("span", { css: Styles.menuItemText }, item.text))));
    };
    const items = ((_a = props.menuItems) !== null && _a !== void 0 ? _a : []).map(renderMenuItem);
    return (isolateComponent("div", { css: Styles.menu(computedState) },
        isolateComponent(Menu, { ariaLabel: "Zap options" },
            isolateComponent("div", { css: Styles.menuInner },
                items,
                hasDetailsMenuItem &&
                    renderMenuItem({
                        text: 'See Zap details',
                        icon: 'miscList',
                        href: (_b = props.detailsLink) === null || _b === void 0 ? void 0 : _b.href,
                        rel: (_c = props.detailsLink) === null || _c === void 0 ? void 0 : _c.rel,
                        onClick: (e) => { var _a; return (_a = props.detailsLink) === null || _a === void 0 ? void 0 : _a.onClick(e, computedState); },
                    })))));
};
const defaultRenderMetaInfo = (props, computedState) => {
    if (props.metaInfo) {
        return props.metaInfo;
    }
    const truncatedServices = computedState.uniqueServices.slice(0, 3);
    const countDiff = computedState.uniqueServices.length - truncatedServices.length;
    return [...truncatedServices, countDiff > 0 && `${countDiff} more`]
        .filter(Boolean)
        .join(' + ');
};
export const ZapCard = ({ authorName, authorAvatarUrl, canClickOutsideToClose, children, color = DEFAULT_COLOR, detailsLink, href, hrefTarget = '_self', label, labelTooltip, layout = 'auto', menuItems = [], metaInfo, onClick, renderAuthor, renderLabel, renderMenu, renderMetaInfo, services, shouldOverlayLink = true, title, }) => {
    const colorValue = useZdsColor(color || DEFAULT_COLOR);
    const allProps = {
        authorName,
        authorAvatarUrl,
        canClickOutsideToClose,
        children,
        color,
        detailsLink,
        href,
        hrefTarget,
        label,
        labelTooltip,
        layout,
        menuItems,
        metaInfo,
        onClick,
        renderAuthor,
        renderLabel,
        renderMenu,
        renderMetaInfo,
        services,
        title,
    };
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const closeMenu = () => setIsMenuOpen(false);
    const [setMenuOutsideClickNode] = useClickOutside({
        onClickOutside: closeMenu,
    });
    let menuNode = null;
    const setMenuNode = (node) => {
        menuNode = node;
        setMenuOutsideClickNode(node);
    };
    const handleClick = (e) => {
        if (!menuNode || !menuNode.contains(e.target)) {
            onClick && onClick(e, computedState);
        }
    };
    const [isExpanded, setIsExpanded] = useState(false);
    const [setExpansionOutsideClickNode] = useClickOutside({
        onClickOutside: () => {
            if (canClickOutsideToClose) {
                setIsExpanded(false);
            }
        },
    });
    const { ref, width } = useResizeObserver();
    const uniqueServices = [
        ...new Set(services.map((service) => {
            return service.name;
        })),
    ];
    const computedState = Object.assign(Object.assign({ isExpanded,
        isMenuOpen, layout: layout }, getComputedColors(colorValue)), { width,
        uniqueServices });
    computedState.layout =
        layout !== 'auto'
            ? layout
            : isCardLayoutWidth(computedState)
                ? 'card'
                : 'line';
    const canExpand = children && isLineLayout(computedState);
    const renderedMenu = renderMenu
        ? renderMenu(allProps, computedState, { closeMenu })
        : defaultRenderMenu(allProps, computedState, { closeMenu });
    const renderedLabel = renderLabel
        ? renderLabel(allProps, computedState)
        : defaultRenderLabel(allProps, computedState);
    const renderedAuthor = renderAuthor
        ? renderAuthor(allProps, computedState)
        : defaultRenderAuthor(allProps, computedState);
    const renderedMetaInfo = renderMetaInfo
        ? renderMetaInfo(allProps, computedState)
        : defaultRenderMetaInfo(allProps, computedState);
    const DetailsLink = detailsLink && (isolateComponent("span", { css: Styles.detailsHref },
        isolateComponent(Link, { href: detailsLink.href, rel: detailsLink.rel, onClick: (e) => detailsLink === null || detailsLink === void 0 ? void 0 : detailsLink.onClick(e, computedState) }, "Details")));
    const link = shouldOverlayLink &&
        (isCardLayout(computedState) || !children) && (isolateComponent(Link, { css: Styles.link, href: href, target: hrefTarget }, title));
    const details = canExpand && (isolateComponent("details", { css: Styles.details, "data-testid": "ZapCard__details", open: isExpanded },
        isolateComponent("summary", { css: Styles.summary, onClick: (e) => {
                e.preventDefault();
                setIsExpanded(!isExpanded);
            } }, "More details"),
        isolateComponent("div", { css: Styles.detailsContent }, children)));
    const cta = isCardOrMegaLayout(computedState) ? (isolateComponent(ZapCardCTA, { computedState: computedState, ariaHidden: !!link, href: href, isLink: true, shouldOverlayLink: shouldOverlayLink, target: hrefTarget })) : isLineLayout(computedState) ? (isolateComponent(ZapCardCTA, { computedState: computedState, ariaHidden: !!link, href: href, isLink: false, shouldOverlayLink: shouldOverlayLink, target: hrefTarget })) : null;
    const serviceIconSize = isMegaLayout(computedState) ? 60 : 40;
    return (isolateComponent("article", { css: Styles.root(allProps, computedState), ref: ref },
        isolateComponent("div", { css: Styles.inner(computedState, allProps), "data-testid": "ZapCard__inner", onClick: handleClick, ref: setExpansionOutsideClickNode },
            isolateComponent("div", { css: Styles.layout(computedState, allProps) },
                isolateComponent("div", { css: Styles.titleArea(computedState) },
                    isolateComponent(Heading, { css: Styles.title(computedState) }, title)),
                renderedAuthor && (isolateComponent("div", { css: Styles.authorArea(computedState) }, renderedAuthor)),
                isolateComponent("div", { css: Styles.labelArea(computedState) },
                    renderedLabel,
                    DetailsLink),
                link,
                cta,
                isolateComponent("div", { css: Styles.menuArea(computedState), ref: setMenuNode },
                    renderedMenu && (isolateComponent("button", { "aria-expanded": isMenuOpen, "aria-label": "More options", css: Styles.menuToggle, onClick: () => setIsMenuOpen(!isMenuOpen), type: "button" },
                        isolateComponent(Icon, { name: "navMoreHoriz", size: 32 }))),
                    isMenuOpen && renderedMenu),
                isolateComponent("div", { css: Styles.servicesArea },
                    isolateComponent(TooltipWrapper, { allowMultilineTooltip: true, content: uniqueServices.join(', '), position: "southwest", shouldCorrectPosition: false, tooltipId: "tooltip-zap-card" }, () => (isolateComponent(ServiceIcons, { ariaLabel: `Zap uses ${uniqueServices.join(', ')}`, maxServices: 3, services: services, size: serviceIconSize, hasBorder: !shouldBeColored(computedState) })))),
                isolateComponent("div", { css: Styles.metaInfoArea(computedState) }, renderedMetaInfo)),
            details)));
};
//# sourceMappingURL=ZapCard.js.map
// @zapier/design-system@6.28.0 (14d5b99c8)
